/**
 * Dev-only console filter for a known, harmless third-party deprecation.
 *
 * `@rjsf/core@4.x` (react-jsonschema-form) declares `defaultProps` on its
 * function-component fields and widgets (StringField, NumberField,
 * SelectWidget, …). React 18.3 logs a deprecation warning for each one:
 *   "<Component>: Support for defaultProps will be removed from function
 *    components in a future major release. Use JavaScript default parameters
 *    instead."
 *
 * This is NOT our bug and has zero runtime effect: defaultProps still works in
 * React 18, and React strips this warning from production builds entirely. rjsf
 * v4 is EOL and does not export its field/widget components, so they cannot be
 * wrapped to drop `defaultProps` — the real resolution is upgrading to
 * `@rjsf/core` v5 (a separate, breaking migration).
 *
 * Until then we silence ONLY this exact message and ONLY for rjsf-style
 * component names (ending in Field / Widget / Input), so a genuine
 * `defaultProps` deprecation on one of OUR own components still surfaces.
 *
 * Remove this file (and its import in index.jsx) once @rjsf/core is on v5+.
 */
// Matches both React's "function components" and "memo components" variants of
// the defaultProps deprecation — rjsf v4 triggers the former, but both are the
// same harmless third-party warning.
const DEPRECATION_MESSAGE = 'Support for defaultProps will be removed from';
const RJSF_COMPONENT_NAME = /(Field|Widget|Input)$/;

export function silenceLibraryDeprecations() {
  // No-op in production: React already strips this warning from prod builds,
  // and we never want to tamper with console.error where real errors matter.
  if (import.meta.env && import.meta.env.PROD) return;

  const originalError = console.error;

  console.error = function filteredConsoleError(...args) {
    const format = typeof args[0] === 'string' ? args[0] : '';

    if (format.includes(DEPRECATION_MESSAGE)) {
      // React passes the offending component's name as the first `%s`
      // substitution argument. Suppress only when it matches rjsf's
      // field/widget naming convention.
      const componentName = typeof args[1] === 'string' ? args[1] : '';
      if (RJSF_COMPONENT_NAME.test(componentName)) return;
    }

    return originalError.apply(this, args);
  };
}
